import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { atom, useAtom } from 'jotai';
import { Button, IconButton } from '@dropbox/dig-components/buttons';
import { UIIcon } from '@dropbox/dig-icons';
import { ChevronLeftLine } from '@dropbox/dig-icons/assets';
import { logPAPEvent } from '../../js/sign-components/common/product-analytics-pipeline';
import { PAP_Click_FormViewToggle } from '@dropbox/pap-events/sign/click_form_view_toggle';
import { useSignerAppParams } from '../signer-experience/signer-experience';
import { useMatchMedia } from '../utils/use-match-media';
import styles from './accessible-document-toggle.module.css';

export const accessibleViewAtom = atom(false);

const messages = defineMessages({
  openAsForm: {
    id: 'a1e862396321ce937b1cb2050c0dbb25bd2a6d01c1a5017fb98c4a0750488a66',
    defaultMessage: 'Open as form',
    description: 'Button to switch from the document to form view.',
  },
  backToDocument: {
    id: '44d948fdc798f0106d804a1fa599fd02bd2c8f08ef4e96728799b5515a4abdf6',
    defaultMessage: 'Back to document',
    description: 'Button to switch from form view back to the document.',
  },
});

export function AccessibleDocumentToggle({ disable = false }) {
  const intl = useIntl();
  const [isAccessibleView, setAccessibleView] = useAtom(accessibleViewAtom);
  const { signatureRequestId } = useSignerAppParams();
  const shouldUseIconButton = useMatchMedia('(max-width: 400px)');

  const toggleView = React.useCallback(() => {
    const isFormView = !isAccessibleView;
    logPAPEvent(
      PAP_Click_FormViewToggle({
        enableFormView: isFormView,
        signatureRequestId,
      }),
    );
    setAccessibleView(isFormView);
  }, [isAccessibleView, setAccessibleView, signatureRequestId]);

  const label = intl.formatMessage(
    isAccessibleView ? messages.backToDocument : messages.openAsForm,
  );
  const icon = isAccessibleView ? (
    <UIIcon src={ChevronLeftLine} />
  ) : (
    <span className={styles.openFormViewIcon} aria-hidden="true" />
  );

  if (shouldUseIconButton) {
    return (
      <IconButton
        variant="outline"
        size="medium"
        disabled={disable}
        onClick={toggleView}
        aria-label={label}
      >
        {icon}
      </IconButton>
    );
  }

  return (
    <Button
      variant="outline"
      size="medium"
      disabled={disable}
      onClick={toggleView}
      withIconStart={icon}
    >
      {label}
    </Button>
  );
}
